"use client"
import { Author as AuthorType } from "@pulsex/types";
import Link from "next/link";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AuthorDetailsView } from "./AuthorDetails/Details";
import pulseConfig from "@/pulsex.config";
import { useBlockPage } from "@pulsex/hooks";
import Spinner from "./Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import AuthorDetails from "./AuthorDetails/Modal/Work";

export interface IAuthorProps {
    author: AuthorType;
    href?: string;
    children: React.ReactNode;
    className?: string;
}

export default function Author({
    href,
    children,
    className,
    author
}: IAuthorProps) {
    const [isOpen, setIsOpen] = useState(false);
    if (!author) return;

    const isAuthorAlMayadeen = author.name === "Al Mayadeen English" || !author.id;
    const Component = isAuthorAlMayadeen ? Div : Link;
    return (
        <>
            <Component
                href={href || "#"}
                scroll={false}
                className={className}
                onClick={() => !isAuthorAlMayadeen && setIsOpen(true)}
            >
                {children}
            </Component>
            {isOpen && <AuthorModal author={author} setIsOpen={setIsOpen} />}
        </>
    );
}

function AuthorModal({
    author,
    setIsOpen
}: {
    author: AuthorType;
    setIsOpen: any;
}) {
    useEffect(() => {
        document.body.style.overflow = "hidden";

        const handleClickOutside = (event: MouseEvent) => {
            const modal = document.getElementById("author-modal");
            if (modal && !modal.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscapeKey);
        return () => {
            document.body.style.overflow = "auto";
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscapeKey);
        };
    }, [setIsOpen]);

    const { data, isLoading } = useBlockPage(pulseConfig, `/${author.id}`);
    if (
        !isLoading &&
        (!data || !data?.blocks?.length || data?.blocks?.length <= 1)
    ) {
        document.body.style.overflow = "auto";
        return "";
    }

    const authorDetailsWidget = data?.blocks![1]?.getMainWidget();
    const authorWorkBlock = data?.blocks![2];

    if (
        !isLoading &&
        (!authorDetailsWidget ||
            !authorDetailsWidget?.posts?.length ||
            !authorWorkBlock)
    ) {
        document.body.style.overflow = "auto";
        return "";
    }

    return createPortal(
        <div
            style={{ zIndex: 100000 }}
            className="fixed inset-0 flex justify-center bg-black bg-opacity-40 pt-10"
        >
            <div id="author-modal" className="author-popup relative rounded-lg bg-white dark:bg-darkbg py-14 px-20 w-[70vw] max-h-[90vh] overflow-y-auto md:py-2 md:px-4 md:w-[95vw]">
                {isLoading ? (
                    <div className="flex h-full justify-center items-center w-full">
                        <Spinner />
                    </div>
                ) : (
                    <>
                        <AuthorDetailsView
                            authorPost={authorDetailsWidget!.posts[0]}
                        />
                        <p className="my-6 text-2xl sm:text-base font-bold dark:text-white lg:mt-2 lg:mb-0">
                            Read more from {author.name}
                        </p>
                        <AuthorDetails
                            block={authorWorkBlock!}
                            className="my-0 p-0"
                            authorId={author.id}
                        />
                    </>
                )}

                {/* close */}
                <FontAwesomeIcon
                    icon={faClose}
                    size={"xl"}
                    className="opacity-70 hover:opacity-100 cursor-pointer absolute top-8 right-8 dark:text-white"
                    onClick={() => setIsOpen(false)}
                />
            </div>
        </div>,
        document.body
    );
}

const Div = ({ children }: { children: React.ReactNode }) => {
    return <div>{children}</div>;
};

